import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface UserPermission {
  resource: string;
  action: string;
  permission_name: string;
}

interface UserRole {
  id: string;
  name: string;
  description: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  userRoles: UserRole[];
  userPermissions: UserPermission[];
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, name: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  hasPermission: (resource: string, action: string) => boolean;
  hasRole: (roleName: string) => boolean;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Development mode flag - set to true to bypass authentication
const DEV_MODE_NO_AUTH = false; // Set to true to bypass auth in development

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);

  const fetchUserRolesAndPermissions = async (userId: string) => {
    try {
      console.log('Fetching user roles and permissions for:', userId);
      
      // Fetch user roles with proper JOIN - handle case where user might not have roles
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_user_roles')
        .select(`
          role_id,
          user_roles (
            id,
            name,
            description
          )
        `)
        .eq('user_id', userId);

      if (rolesError) {
        console.error('Error fetching user roles:', rolesError);
        // Don't return here - continue to try fetching permissions
        setUserRoles([]);
      } else {
        console.log('Roles data received:', rolesData);

        // Fix the data structure - handle potential null values
        const roles = rolesData?.map((item: any) => {
          // Check if user_roles exists and is not null
          if (item.user_roles && typeof item.user_roles === 'object' && !Array.isArray(item.user_roles)) {
            return {
              id: item.user_roles.id,
              name: item.user_roles.name,
              description: item.user_roles.description
            };
          }
          return null;
        }).filter(role => role !== null) || [];
        
        setUserRoles(roles as UserRole[]);
        console.log('User roles set:', roles);
      }

      // Fetch user permissions - handle case where function might not return data
      try {
        const { data: permissionsData, error: permissionsError } = await supabase
          .rpc('get_user_permissions', { user_uuid: userId });

        if (permissionsError) {
          console.error('Error fetching user permissions:', permissionsError);
          setUserPermissions([]);
        } else {
          console.log('Permissions data received:', permissionsData);
          setUserPermissions(permissionsData || []);
          console.log('User permissions set:', permissionsData);
        }
      } catch (permError) {
        console.error('Error calling get_user_permissions RPC:', permError);
        setUserPermissions([]);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Set empty arrays to prevent undefined issues
      setUserRoles([]);
      setUserPermissions([]);
    }
  };

  const refreshUserData = async () => {
    if (user) {
      await fetchUserRolesAndPermissions(user.id);
    }
  };

  useEffect(() => {
    let isMounted = true;

    // Development mode - bypass authentication
    if (DEV_MODE_NO_AUTH) {
      console.warn('DEV MODE: Authentication bypassed');
      // Create a mock user for development
      const mockUser = {
        id: 'dev-user-123',
        email: 'dev@example.com',
        app_metadata: {},
        user_metadata: { name: 'Dev User' },
        aud: 'authenticated',
        created_at: new Date().toISOString()
      } as User;
      
      setUser(mockUser);
      setSession(null);
      setUserRoles([{ id: '1', name: 'admin', description: 'Administrator' }]);
      setUserPermissions([]);
      setLoading(false);
      return;
    }

    const initializeAuth = async () => {
      try {
        // Set a timeout to prevent infinite loading
        const timeoutId = setTimeout(() => {
          if (isMounted) {
            console.warn('Auth initialization timeout - proceeding without roles/permissions');
            setLoading(false);
          }
        }, 5000); // 5 second timeout

        // Get initial session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!isMounted) {
          clearTimeout(timeoutId);
          return;
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Don't await this - let it run but with its own error handling
          fetchUserRolesAndPermissions(session.user.id).catch(err => {
            console.error('Non-blocking error fetching user data:', err);
          });
        }
        
        clearTimeout(timeoutId);
        if (isMounted) {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      try {
        if (!isMounted) return;
        
        console.log('Auth state changed:', event);
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Don't await - let it run asynchronously
          fetchUserRolesAndPermissions(session.user.id).catch(err => {
            console.error('Non-blocking error fetching user data on auth change:', err);
          });
        } else {
          setUserRoles([]);
          setUserPermissions([]);
        }
        
        if (isMounted) {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
        if (isMounted) {
          setLoading(false);
        }
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { error };
      }

      return { error: null };
    } catch (error) {
      // Return a simple error object that matches the interface
      return { error: { message: 'An unexpected error occurred' } as any };
    }
  };

  const signUp = async (email: string, password: string, name: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            role: 'viewer', // Default role for new users
          },
        },
      });

      if (error) {
        return { error };
      }

      return { error: null };
    } catch (error) {
      // Return a simple error object that matches the interface
      return { error: { message: 'An unexpected error occurred' } as any };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        return { error };
      }

      return { error: null };
    } catch (error) {
      // Return a simple error object that matches the interface
      return { error: { message: 'An unexpected error occurred' } as any };
    }
  };

  const hasPermission = (resource: string, action: string): boolean => {
    return userPermissions.some(
      permission => permission.resource === resource && permission.action === action
    );
  };

  const hasRole = (roleName: string): boolean => {
    return userRoles.some(role => role.name === roleName);
  };

  const value = {
    user,
    session,
    loading,
    userRoles,
    userPermissions,
    signIn,
    signUp,
    signOut,
    resetPassword,
    hasPermission,
    hasRole,
    refreshUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
